import { useEffect, useState } from "react";
import {
  Dropdown,
  TextInput,
  FileUpload,
  DatePicker,
} from "../../../components/ui/inputs";
import verificationService from "../service/verification.service";
import {
  DocumentType,
  DocumentTypeDetail,
} from "../models/response/documentType";
import localization from "../../../config/localization";
import { Button } from "../../../components/ui/Button";
import { BlobContainerConsts } from "../../../models/constants/blobContainerConsts";
import { BLOB_IMAGE_URL } from "../../../config/appConfig";
import { DocumentStatus } from "../../../models/enums/documentStatus";
import { formatDateTimeWIthTimezone } from "../../../utils/dateTimeHelper";
import userDeviceStore from "../../../store/device.store";
import { Card, CardBody, Header2 } from "../../../components/ui";
import { DeviceType } from "../../../models/constants/deviceType";
import { userPendingStore } from "../../users/store/userPendingData.store";
import { useRealTimeStore } from "../../../store/signalrRealTime.store";
import { FeedbackModal as SuccessModal } from "../../../components/ui/modals/FeedbackModal";
import { useAppDispatch } from "../../../store/hooks";
import { changeLoaderState } from "../../../reducers/appStateReducer";
import { increaseInboxUnreadCount } from "../../users/reducers/userProfileReducer";

export function Identification() {
  const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
  const dispatch = useAppDispatch();

  const [documentTypes, setDocumentTypes] =
    useState<DocumentTypeDetail | null>(null);
  const [selectedDocumentType, setSelectedDocumentType] =
    useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] =
    useState<DocumentType | null>(null);

  const [documentNumber, setDocumentNumber] = useState("");
  const [expirationDate, setExpirationDate] = useState<string | null>(null);

  const [frontFile, setFrontFile] = useState<File | null>(null);
  const [existingFrontFileName, setExistingFrontFileName] =
    useState<string | null>(null);
  const [frontPreview, setFrontPreview] = useState<string | null>(null);
  const [frontError, setFrontError] = useState<string | null>(null);

  const [backFile, setBackFile] = useState<File | null>(null);
  const [existingBackFileName, setExistingBackFileName] =
    useState<string | null>(null);
  const [backPreview, setBackPreview] = useState<string | null>(null);
  const [backError, setBackError] = useState<string | null>(null);

  const [recordId, setRecordId] = useState<string>(EMPTY_GUID);
  const [status, setStatus] = useState<DocumentStatus | null>(null);
  const [remarks, setRemarks] = useState<string | null>(null);
  const [isEntryLock, setIsEntryLock] = useState(false);

  const [successModalTitle, setSuccessModalTitle] = useState<string>("");
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);

  const latestDocumentUpdate = useRealTimeStore(
    (state) => state.latestDocumentUpdate
  );

  const deviceType = userDeviceStore((state) => state.deviceType);

  useEffect(() => {
    const initializeData = async () => {
      dispatch(changeLoaderState(true));
      try {
        const [documentTypesResponse] = await Promise.all([
          verificationService.getDocumentTypes(),
          load(),
        ]);

        if (documentTypesResponse?.isSuccess) {
          setDocumentTypes(documentTypesResponse.data ?? null);
        }
      } finally {
        setIsInitialDataLoaded(true);
        dispatch(changeLoaderState(false));
      }
    };

    initializeData();
  }, [dispatch]);

  const load = async () => {
    const res = await verificationService.retrieveUserDocument();
    if (!res?.isSuccess || !res.data) return;

    const { data } = res;

    setRecordId(data.id ?? EMPTY_GUID);
    setStatus((data.status as DocumentStatus) ?? null);
    setRemarks(data.remarks ?? null);
    setSelectedDocumentType(data.proofId ?? null);
    setDocumentNumber(data.documentNo ?? "");
    setExpirationDate(data.expirationDate ?? null);

    if (
      data.status?.toLowerCase() === DocumentStatus.Approved.toLowerCase()
    ) {
      setIsEntryLock(true);
    }

    if (data.frontSideImage && data.status?.toLowerCase() !== DocumentStatus.Rejected.toLowerCase()) {
      setExistingFrontFileName(data.frontSideImage);
      setFrontPreview(
        `${BLOB_IMAGE_URL}${BlobContainerConsts.UserDocuments}/${data.frontSideImage}`
      );
    }

    if (data.backSideImage && data.status?.toLowerCase() !== DocumentStatus.Rejected.toLowerCase()) {
      setExistingBackFileName(data.backSideImage);
      setBackPreview(
        `${BLOB_IMAGE_URL}${BlobContainerConsts.UserDocuments}/${data.backSideImage}`
      );
    }
  };

  useEffect(() => {
    if (!latestDocumentUpdate) return;
    if (!recordId || recordId === EMPTY_GUID) return;
    if (latestDocumentUpdate.id?.toString() !== recordId?.toString()) return;

    const latestStatus = latestDocumentUpdate.status as DocumentStatus | null;

    setStatus(latestStatus);
    setRemarks(latestDocumentUpdate.remarks ?? null);

    if (latestStatus === DocumentStatus.Rejected) {
      setFrontFile(null);
      setExistingFrontFileName(null);
      setFrontPreview(null);
      setBackFile(null);
      setExistingBackFileName(null);
      setBackPreview(null);
    }
  }, [latestDocumentUpdate, recordId]);

  useEffect(() => {
    if (!selectedDocumentType || !documentTypes) return;

    const doc = documentTypes.details.find(
      (d) => d.id === selectedDocumentType
    );
    if (!doc) return;

    setSelectedDocument(doc);
    if (!doc.isHavingExpireDate) setExpirationDate(null);
  }, [selectedDocumentType, documentTypes]);

  const documentError = (() => {
    if (!selectedDocument) return null;
    if (!documentNumber) return localization.enter_document_number;

    try {
      const regex = new RegExp(
        selectedDocument.validationExpression.replace("\\\\", "\\")
      );
      if (!regex.test(documentNumber.toUpperCase())) {
        return localization.validate_documenttype.replace(
          "@documenttype",
          selectedDocument.name
        );
      }
    } catch {
      return "Invalid document format";
    }
    return null;
  })();

  const expiryError =
    selectedDocument?.isHavingExpireDate &&
      expirationDate &&
      new Date(expirationDate) < new Date()
      ? localization.expiry_date_error
      : null;

  const validateImage = (file: File) => {
    if (!["image/jpeg", "image/png"].includes(file.type))
      return localization.img_extention;
    if (file.size / 1_000_000 > 5) return localization.img_size;
    return null;
  };

  const handleFrontSelect = (file: File | null) => {
    if (!file) return;
    const err = validateImage(file);
    if (err) {
      setFrontError(err);
      setFrontFile(null);
      setFrontPreview(null);
      return;
    }
    setFrontError(null);
    setFrontFile(file);
    setFrontPreview(URL.createObjectURL(file));
  };

  const handleBackSelect = (file: File | null) => {
    if (!file) return;
    const err = validateImage(file);
    if (err) {
      setBackError(err);
      setBackFile(null);
      setBackPreview(null);
      return;
    }
    setBackError(null);
    setBackFile(file);
    setBackPreview(URL.createObjectURL(file));
  };

  const isBackRequired = selectedDocument?.isHavingBackImage === true;
  const isLocked =
    status?.toLowerCase() === DocumentStatus.Approved.toLowerCase();

  const isFormValid =
    selectedDocumentType &&
    documentNumber &&
    (frontFile || existingFrontFileName) &&
    (!isBackRequired || backFile || existingBackFileName) &&
    !documentError &&
    !expiryError &&
    !frontError &&
    !backError;

  const handleSubmit = async () => {
    if (!isFormValid) return;

    let frontImage = existingFrontFileName;
    let backImage = existingBackFileName;

    if (frontFile) {
      const res = await verificationService.uploadFrontDocument({
        file: frontFile,
        oldFileName: existingFrontFileName ?? "",
        fileName: frontFile.name,
      });
      if (!res.isSuccess) return;
      frontImage = res.data?.fileName ?? null;
    }

    if (backFile) {
      const res = await verificationService.uploadBackDocument({
        file: backFile,
        oldFileName: existingBackFileName ?? "",
        fileName: backFile.name,
      });
      if (!res.isSuccess) return;
      backImage = res.data?.fileName ?? null;
    }

    var response = await verificationService.userDocumentInsert({
      proofId: selectedDocumentType!,
      documentNo: documentNumber,
      isExpiryDate: selectedDocument?.isHavingExpireDate ?? false,
      expirationDate: selectedDocument?.isHavingExpireDate
        ? formatDateTimeWIthTimezone(expirationDate!)
        : null,
      frontSideImage: frontImage,
      backSideImage: backImage,
      isEntryLock,
      status,
      remarks,
      recordId,
    });

    if (response.isSuccess) {
      userPendingStore.getState().fetchPendingData();
      dispatch(increaseInboxUnreadCount());
      setSuccessModalTitle(localization.document_upload_successfully);
      setIsSuccessModalOpen(true);
    }
  };

  const handleCloseSuccessModal = async () => {
    await load();
    setSuccessModalTitle("");
    setIsSuccessModalOpen(false);
  };

  let content = (
    <div className="space-y-5">
      {deviceType === DeviceType.Web && <Header2 className="text-white text-center mb-6">{localization.details_verification}</Header2>}
      <div className="flex flex-col gap-4 md:grid md:grid-cols-2 md:gap-6">
        <Dropdown
          label={localization.choose_document_type}
          isRequired
          disabled={isLocked}
          options={
            documentTypes?.details.map((doc) => ({
              label: localization.getString(doc.name.toLowerCase()),
              value: doc.id,
            })) ?? []
          }
          value={selectedDocumentType ?? undefined}
          onChange={setSelectedDocumentType}
        />

        <TextInput
          label={localization.document_number}
          value={documentNumber}
          placeholder={localization.enter_document_number}
          maxLength={selectedDocument?.maxLength ?? 0}
          isRequired
          error={documentError ?? undefined}
          disabled={isLocked}
          onChange={(e) => setDocumentNumber(e.target.value.toUpperCase())}
        />

        {selectedDocument?.isHavingExpireDate && (
          <DatePicker
            label={localization.expiry_date}
            value={expirationDate ?? undefined}
            isRequired
            disabled={isLocked}
            onChange={setExpirationDate}
            error={expiryError ?? undefined}
            alwaysOpenDown={true}
          />
        )}

        {
          status && (
            <TextInput
              label={localization.status}
              value={status ?? undefined}
              disabled={true}
              readOnly={true}
            />
          )
        }

        {
          remarks && status?.toLowerCase() === DocumentStatus.Rejected.toLowerCase() && (
            <TextInput
              label={localization.remarks}
              value={remarks ?? undefined}
              disabled={true}
              readOnly={true}
            />
          )
        }


        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <FileUpload
            label={localization.upload_document_front}
            required
            preview={frontPreview}
            helperText={`${localization.upload_image_lbl} ${localization.upload_image_lbl_size}`}
            error={frontError ?? undefined}
            canPreview={true}
            disabled={isLocked}
            onSelect={handleFrontSelect}
            onRemove={() => {
              setFrontFile(null);
              setFrontPreview(null);
            }}
          />

          <FileUpload
            label={localization.upload_document_back}
            required={isBackRequired}
            preview={backPreview}
            helperText={`${localization.upload_image_lbl} ${localization.upload_image_lbl_size}`}
            error={backError ?? undefined}
            disabled={isLocked}
            canPreview={true}
            onSelect={handleBackSelect}
            onRemove={() => {
              setBackFile(null);
              setBackPreview(null);
            }}
          />
        </div>
      </div>

      {!isLocked && (
        <div className="flex justify-center">
          <Button
            disabled={!isFormValid}
            onClick={handleSubmit}
            className="w-full md:w-1/4 mx-auto py-4 rounded-md text-white bg-primary-gradient disabled:opacity-50 mt-2"
          >
            <span className="uppercase">{localization.submit}</span>
          </Button>
        </div>
      )}

      {isSuccessModalOpen && (
        <SuccessModal
          isOpen={isSuccessModalOpen}
          title={successModalTitle}
          autoClose={true}
          descriptionClassName="text-center text-lg font-normal text-[#111111]"
          icon={"/icons/ic-success-modal.svg"}
          blurColor="bg-[#D4FFDECC]"
          onClose={() => handleCloseSuccessModal()}
        />
      )}
    </div>
  )

  if (!isInitialDataLoaded) {
    return null;
  }

  return deviceType !== DeviceType.Web ? (
    content
  ) : (
    <Card>
      <CardBody showGradient={true}>
        <div className="w-full max-w-4xl mx-auto">{content}</div>
      </CardBody>
    </Card>
  );
}
